<template>
    <div class="tw:px-6 tw:py-6">
        <!-- Error state -->
        <div v-if="error" class="tw:mb-6 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:p-4 tw:flex tw:items-center tw:gap-3">
            <span class="tw:text-sm tw:text-red-700">{{ error }}</span>
        </div>

        <!-- Loading skeleton -->
        <template v-if="loading">
            <div class="tw:mb-8">
                <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.economic.section_abonos') }}</h3>
                <div class="tw:grid tw:grid-cols-3 tw:gap-4 tw:mb-6">
                    <StatsCard v-for="i in 3" :key="'skeleton-abono-' + i" :title="''" :value="''" :loading="true" />
                </div>
                <div class="tw:grid tw:grid-cols-1 lg:tw:grid-cols-2 tw:gap-4">
                    <div v-for="i in 3" :key="'skeleton-chart-ab-' + i" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                        <div class="tw:flex tw:items-center tw:justify-center tw:h-64">
                            <svg class="tw:animate-spin tw:h-8 tw:w-8 tw:text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="tw:opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                                <path class="tw:opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.economic.section_entradas') }}</h3>
                <div class="tw:grid tw:grid-cols-3 tw:gap-4 tw:mb-6">
                    <StatsCard v-for="i in 3" :key="'skeleton-entrada-' + i" :title="''" :value="''" :loading="true" />
                </div>
                <div class="tw:grid tw:grid-cols-1 lg:tw:grid-cols-2 tw:gap-4">
                    <div v-for="i in 3" :key="'skeleton-chart-en-' + i" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                        <div class="tw:flex tw:items-center tw:justify-center tw:h-64">
                            <svg class="tw:animate-spin tw:h-8 tw:w-8 tw:text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="tw:opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                                <path class="tw:opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <!-- Data -->
        <template v-else-if="kpis">
            <!-- Abonos Section -->
            <div class="tw:mb-8">
                <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.economic.section_abonos') }}</h3>
                <p class="tw:text-sm tw:text-gray-500 tw:mb-6">{{ $t('back.dashboard.economic.section_abonos_desc') }}</p>

                <div v-if="!isMultiSeason" class="tw:grid tw:grid-cols-3 tw:gap-4 tw:mb-6 tw:max-w-4xl tw:mx-auto">
                    <StatsCard
                        :title="$t('back.dashboard.economic.kpis.ingresos_abonos')"
                        :value="formatCurrency(kpis.abonos.ingresos_temporada)"
                        variant="success"
                    />
                    <StatsCard
                        :title="$t('back.dashboard.economic.kpis.numero_abonados')"
                        :value="formatNumber(kpis.abonos.numero_abonados_temporada)"
                        variant="primary"
                    />
                    <StatsCard
                        :title="$t('back.dashboard.economic.kpis.precio_medio_abono')"
                        :value="formatCurrency(kpis.abonos.precio_medio_temporada)"
                        variant="warning"
                    />
                </div>

                <!-- Abonos per-season breakdown -->
                <div v-if="isMultiSeason && seasonBreakdown?.abonos?.length" class="tw:mb-6 tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:bg-white tw:max-w-2xl tw:mx-auto">
                    <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-3">{{ $t('back.dashboard.economic.breakdown_title') }}</h4>
                    <table class="tw:w-full tw:text-sm">
                        <thead><tr class="tw:text-left tw:text-gray-500 tw:text-xs"><th>{{ $t('back.dashboard.economic.season_label') }}</th><th class="tw:text-right">{{ $t('back.dashboard.economic.kpis.ingresos_abonos') }}</th><th class="tw:text-right">{{ $t('back.dashboard.economic.kpis.numero_abonados') }}</th></tr></thead>
                        <tbody>
                            <tr v-for="row in seasonBreakdown.abonos" :key="row.season" class="tw:border-t tw:border-gray-100">
                                <td class="tw:py-1.5">{{ row.season }}</td>
                                <td class="tw:py-1.5 tw:text-right">{{ formatCurrency(row.ingresos) }}</td>
                                <td class="tw:py-1.5 tw:text-right">{{ formatNumber(row.count) }}</td>
                            </tr>
                            <tr class="tw:border-t-2 tw:border-gray-300 tw:font-bold">
                                <td class="tw:py-2">{{ $t('back.dashboard.economic.breakdown_total') }}</td>
                                <td class="tw:py-2 tw:text-right">{{ formatCurrency(seasonBreakdown.totals.abonos.ingresos) }}</td>
                                <td class="tw:py-2 tw:text-right">{{ formatNumber(seasonBreakdown.totals.abonos.count) }}</td>
                            </tr>
                            <tr class="tw:text-gray-600">
                                <td class="tw:py-2">{{ $t('back.dashboard.economic.average') }}</td>
                                <td class="tw:py-2 tw:text-right">{{ formatCurrency(seasonBreakdown.totals.abonos.ingresos_medios) }}</td>
                                <td class="tw:py-2 tw:text-right">{{ formatNumber(Math.round(seasonBreakdown.totals.abonos.count / (seasonBreakdown.abonos.length || 1))) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Abonos charts -->
                <div class="tw:grid tw:grid-cols-1 lg:tw:grid-cols-2 tw:gap-4">
                    <div v-for="cfg in abonosCharts" :key="cfg.field" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                        <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t(cfg.labelKey) }}</h3>
                        <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t(cfg.descriptionKey) }}</p>
                        <div v-if="hasChartData(cfg.field)">
                            <Chart
                                :type="cfg.resolvedType"
                                :data="chartData.get(cfg.field)?.data || []"
                                :options="getChartOptions(cfg.field)"
                                :height="280"
                            />
                        </div>
                        <div v-else class="tw:h-64 tw:flex tw:items-center tw:justify-center tw:text-gray-400 tw:text-sm">
                            {{ $t('back.dashboard.economic.empty') }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Entradas Section -->
            <div>
                <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.economic.section_entradas') }}</h3>
                <p class="tw:text-sm tw:text-gray-500 tw:mb-6">{{ $t('back.dashboard.economic.section_entradas_desc') }}</p>

                <div v-if="!isMultiSeason" class="tw:grid tw:grid-cols-3 tw:gap-4 tw:mb-6 tw:max-w-4xl tw:mx-auto">
                    <StatsCard
                        :title="$t('back.dashboard.economic.kpis.vendidas_temporada')"
                        :value="formatNumber(kpis.entradas.vendidas_temporada)"
                        variant="success"
                    />
                    <StatsCard
                        :title="$t('back.dashboard.economic.kpis.precio_medio_entradas')"
                        :value="formatCurrency(kpis.entradas.precio_medio_temporada)"
                        variant="primary"
                    />
                    <StatsCard
                        :title="$t('back.dashboard.economic.kpis.ingresos_entradas')"
                        :value="formatCurrency(kpis.entradas.ingresos_temporada)"
                        variant="warning"
                    />
                </div>

                <!-- Entradas per-season breakdown -->
                <div v-if="isMultiSeason && seasonBreakdown?.entradas?.length" class="tw:mb-6 tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:bg-white tw:max-w-2xl tw:mx-auto">
                    <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-3">{{ $t('back.dashboard.economic.breakdown_title') }}</h4>
                    <table class="tw:w-full tw:text-sm">
                        <thead><tr class="tw:text-left tw:text-gray-500 tw:text-xs"><th>{{ $t('back.dashboard.economic.season_label') }}</th><th class="tw:text-right">{{ $t('back.dashboard.economic.kpis.ingresos_entradas') }}</th><th class="tw:text-right">{{ $t('back.dashboard.economic.kpis.vendidas_temporada') }}</th><th class="tw:text-right">{{ $t('back.dashboard.attendance_avg') }}</th></tr></thead>
                        <tbody>
                            <tr v-for="row in seasonBreakdown.entradas" :key="row.season" class="tw:border-t tw:border-gray-100">
                                <td class="tw:py-1.5">{{ row.season }}</td>
                                <td class="tw:py-1.5 tw:text-right">{{ formatCurrency(row.ingresos) }}</td>
                                <td class="tw:py-1.5 tw:text-right">{{ formatNumber(row.vendidas) }}</td>
                                <td class="tw:py-1.5 tw:text-right">{{ formatNumber(row.asistencia) }}</td>
                            </tr>
                            <tr class="tw:border-t-2 tw:border-gray-300 tw:font-bold">
                                <td class="tw:py-2">{{ $t('back.dashboard.economic.breakdown_total') }}</td>
                                <td class="tw:py-2 tw:text-right">{{ formatCurrency(seasonBreakdown.totals.entradas.ingresos) }}</td>
                                <td class="tw:py-2 tw:text-right">{{ formatNumber(seasonBreakdown.totals.entradas.vendidas) }}</td>
                                <td class="tw:py-2 tw:text-right">{{ formatNumber(seasonBreakdown.totals.entradas.asistencia) }}</td>
                            </tr>
                            <tr class="tw:text-gray-600">
                                <td class="tw:py-2">{{ $t('back.dashboard.economic.average') }}</td>
                                <td class="tw:py-2 tw:text-right">{{ formatCurrency(seasonBreakdown.totals.entradas.ingresos_medios) }}</td>
                                <td class="tw:py-2 tw:text-right">{{ formatNumber(Math.round(seasonBreakdown.totals.entradas.vendidas / (seasonBreakdown.entradas.length || 1))) }}</td>
                                <td class="tw:py-2 tw:text-right">{{ formatNumber(Math.round(seasonBreakdown.totals.entradas.asistencia_media)) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Entradas charts -->
                <div class="tw:grid tw:grid-cols-1 lg:tw:grid-cols-2 tw:gap-4">
                    <div v-for="cfg in entradasCharts" :key="cfg.field" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                        <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t(cfg.labelKey) }}</h3>
                        <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t(cfg.descriptionKey) }}</p>
                        <div v-if="hasChartData(cfg.field)">
                            <Chart
                                :type="cfg.resolvedType"
                                :data="chartData.get(cfg.field)?.data || []"
                                :options="getChartOptions(cfg.field)"
                                :height="280"
                            />
                        </div>
                        <div v-else class="tw:h-64 tw:flex tw:items-center tw:justify-center tw:text-gray-400 tw:text-sm">
                            {{ $t('back.dashboard.economic.empty') }}
                        </div>
                    </div>
                </div>

                <!-- Geografía - tabla en lugar de gráfico -->
                <div class="tw:mt-8">
                    <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.economic.charts.compradores_geografia.title') }}</h3>
                    <p class="tw:text-sm tw:text-gray-500 tw:mb-6">{{ $t('back.dashboard.economic.charts.compradores_geografia.description') }}</p>

                    <div class="tw:grid tw:grid-cols-1 lg:tw:grid-cols-3 tw:gap-6">
                        <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:bg-white" v-if="geographyTableData.provincias?.length">
                            <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-3">{{ $t('back.dashboard.economic.geography_provincias') }}</h4>
                            <table class="tw:w-full tw:text-sm">
                                <thead><tr class="tw:text-left tw:text-gray-500 tw:text-xs"><th>#</th><th>{{ $t('back.dashboard.economic.geography_provincias') }}</th><th class="tw:text-right">{{ $t('back.dashboard.economic.kpis.numero_abonados') }}</th></tr></thead>
                                <tbody>
                                    <tr v-for="(row, i) in geographyTableData.provincias" :key="row.label" class="tw:border-t tw:border-gray-100">
                                        <td class="tw:py-1.5 tw:text-gray-400">{{ i + 1 }}</td>
                                        <td class="tw:py-1.5">{{ row.label }}</td>
                                        <td class="tw:py-1.5 tw:text-right tw:font-medium">{{ formatNumber(row.total) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:bg-white" v-if="geographyTableData.localidades?.length">
                            <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-3">{{ $t('back.dashboard.economic.geography_localidades') }}</h4>
                            <table class="tw:w-full tw:text-sm">
                                <thead><tr class="tw:text-left tw:text-gray-500 tw:text-xs"><th>#</th><th>{{ $t('back.dashboard.economic.geography_localidades') }}</th><th class="tw:text-right">{{ $t('back.dashboard.economic.kpis.numero_abonados') }}</th></tr></thead>
                                <tbody>
                                    <tr v-for="(row, i) in geographyTableData.localidades" :key="row.label" class="tw:border-t tw:border-gray-100">
                                        <td class="tw:py-1.5 tw:text-gray-400">{{ i + 1 }}</td>
                                        <td class="tw:py-1.5">{{ row.label }}</td>
                                        <td class="tw:py-1.5 tw:text-right tw:font-medium">{{ formatNumber(row.total) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:bg-white" v-if="geographyTableData.codigos_postales?.length">
                            <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-3">{{ $t('back.dashboard.economic.geography_codigos_postales') }}</h4>
                            <table class="tw:w-full tw:text-sm">
                                <thead><tr class="tw:text-left tw:text-gray-500 tw:text-xs"><th>#</th><th>{{ $t('back.dashboard.economic.geography_codigos_postales') }}</th><th class="tw:text-right">{{ $t('back.dashboard.economic.kpis.numero_abonados') }}</th></tr></thead>
                                <tbody>
                                    <tr v-for="(row, i) in geographyTableData.codigos_postales" :key="row.label" class="tw:border-t tw:border-gray-100">
                                        <td class="tw:py-1.5 tw:text-gray-400">{{ i + 1 }}</td>
                                        <td class="tw:py-1.5">{{ row.label }}</td>
                                        <td class="tw:py-1.5 tw:text-right tw:font-medium">{{ formatNumber(row.total) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <!-- Empty state -->
        <div v-else class="tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-lg tw:p-8 tw:text-center">
            <p class="tw:text-sm tw:text-gray-500">{{ $t('back.dashboard.economic.empty') }}</p>
        </div>
    </div>
</template>

<script setup>
    import { computed, toRef } from 'vue';
    import { useEconomic } from '@/composables/admin/dashboard/useEconomic';
    import { formatCurrency, formatNumber } from '@/utils/format';
    import StatsCard from '@/modules/admin/ui/StatsCard.vue';
    import Chart from '@/modules/admin/ui/Chart.vue';

    const props = defineProps({
        seasonIds: {
            type: Array,
            required: true,
        },
        seasons: {
            type: Array,
            required: true,
        },
    });



    const seasonIdsRef = toRef(props, 'seasonIds');
    const seasonsRef = toRef(props, 'seasons');

    const {
        kpis,
        chartData,
        chartConfigs,
        loading,
        error,
        isMultiSeason,
        geographyTableData,
        seasonBreakdown,
        getChartOptions,
    } = useEconomic(seasonIdsRef, seasonsRef);

    /** Charts del grupo abonos */
    const abonosCharts = computed(() =>
        chartConfigs.value.filter(c => c.group === 'abonos')
    );

    /** Charts del grupo entradas */
    const entradasCharts = computed(() =>
        chartConfigs.value.filter(c => c.group === 'entradas')
    );

    /** Verifica si un chart tiene datos para renderizar */
    function hasChartData(field) {
        const entry = chartData.value.get(field);
        return entry && entry.data && entry.data.length > 1;
    }
</script>
